// GBPOptimizationPanel — the "AI Insight" screen.
//
// Renders the gbp-optimize report exactly as it looks standalone: a single
// full-width, style-isolated iframe that shows the report WITH ITS OWN
// horizontal tab bar (Data & Analysis / Result & Actions / Google Reviews
// Insights). We deliberately do NOT postMessage `gbiEmbed`, so the report never
// hides its own chrome — the in-system view is a 1:1 replica of the standalone
// HTML. The report posts its document height back so the iframe auto-sizes (no
// inner scrollbar). GBI keeps only a slim header: the Health badge + a Markdown
// download for the team.
import { useEffect, useRef, useState } from 'react';
import { GBICard, GBIBadge, T, sharedStyles } from './Shared';

interface GbpReport {
  html: string;
  markdown: string | null;
  summary: string | null;
  score: number | null;
  model: string | null;
  generatedAt: number;
}

interface AutomationStatus {
  status: string;
  currentStep: string | null;
  nextRetryAt: number | null;
  error: { code: string; message: string | null } | null;
}

export function GBPOptimizationPanel({ clientId, clientName }: { clientId: string; clientName?: string; readOnly?: boolean }) {
  // undefined = loading, null = no report yet, object = report
  const [report, setReport] = useState<GbpReport | null | undefined>(undefined);
  const [height, setHeight] = useState(1600);
  const iframeRef = useRef<HTMLIFrameElement | null>(null);
  const [automation, setAutomation] = useState<AutomationStatus | null>(null);

  useEffect(() => {
    let alive = true;
    setReport(undefined);
    fetch(`/api/clients/${clientId}/gbp-report`, { credentials: 'same-origin' })
      .then(async (r): Promise<{ report: GbpReport | null }> => (r.ok ? r.json() : { report: null }))
      .then(d => { if (alive) setReport(d.report ?? null); })
      .catch(() => { if (alive) setReport(null); });
    return () => { alive = false; };
  }, [clientId]);

  useEffect(() => {
    if (report !== null) return;
    let alive = true;
    let timer = 0;
    const poll = () => {
      fetch(`/api/clients/${clientId}/automation-status`, { credentials: 'same-origin' })
        .then(async r => r.ok ? await r.json() as { job: AutomationStatus | null } : { job: null })
        .then(data => {
          if (!alive) return;
          setAutomation(data.job);
          if (data.job?.status === 'completed') {
            fetch(`/api/clients/${clientId}/gbp-report`, { credentials: 'same-origin' })
              .then(r => r.json() as Promise<{ report: GbpReport | null }>).then(d => { if (alive && d.report) setReport(d.report); });
          } else if (data.job && !['failed', 'needs_input'].includes(data.job.status)) {
            timer = window.setTimeout(poll, 5000);
          }
        }).catch(() => { if (alive) timer = window.setTimeout(poll, 10000); });
    };
    poll();
    return () => { alive = false; window.clearTimeout(timer); };
  }, [clientId, report]);

  // The embedded report posts its document height so we can size the iframe to
  // fit (no inner scrollbar). It posts on load, on resize, and whenever its own
  // content height changes (e.g. switching its tabs) via a ResizeObserver.
  useEffect(() => {
    const onMsg = (e: MessageEvent) => {
      const d = e.data as { gbiHeight?: number } | null;
      if (d && typeof d.gbiHeight === 'number') setHeight(Math.max(640, d.gbiHeight + 8));
    };
    window.addEventListener('message', onMsg);
    return () => window.removeEventListener('message', onMsg);
  }, []);

  // Full-bleed: the report supplies its own page chrome + insets, so the panel
  // adds none horizontally — that's what makes it read as a full-screen page
  // rather than an HTML "stuffed into a box".
  const wrap = {
    flex: 1, minWidth: 0, height: '100%', minHeight: 0, overflowY: 'auto' as const,
    background: T.bgSurface, display: 'flex', flexDirection: 'column' as const,
  };

  if (report === undefined) {
    return <div style={{ ...wrap, padding: '28px 32px' }}><div style={{ color: T.fgMuted, fontSize: 14 }}>Loading…</div></div>;
  }

  if (report === null) {
    return (
      <div style={{ ...wrap, padding: '28px 32px' }}>
        <GBICard style={{ marginTop: 8, textAlign: 'center', padding: '52px 20px' }}>
          <div style={{ color: T.fgSecondary, fontSize: 15, fontWeight: 600 }}>
            {automation ? statusLabel(automation) : 'Preparing optimization report'}
          </div>
          <div style={{ color: T.fgMuted, fontSize: 13, marginTop: 6 }}>
            {automation?.error
              ? `${automation.error.code}: ${automation.error.message ?? 'Retry information is available to the admin.'}`
              : `The report for ${clientName ?? 'this profile'} will appear here automatically when every required input is complete.`}
          </div>
        </GBICard>
      </div>
    );
  }

  return (
    <div style={wrap}>
      {/* Slim header — Health badge + Markdown download only. The report carries
          its own title/tabs, so we keep GBI chrome to a single thin bar. */}
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap',
        padding: '12px 20px', borderBottom: `1px solid ${T.border}`, background: '#fff', flexShrink: 0,
      }}>
        <div style={{ fontSize: 12.5, color: T.fgMuted }}>
          GBP Optimization · <span style={{ color: T.fgSecondary, fontWeight: 600 }}>{clientName ?? 'this profile'}</span>
          {report.generatedAt ? ` · updated ${new Date(report.generatedAt).toLocaleDateString()}` : ''}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          {report.score != null && (
            <GBIBadge color={report.score >= 75 ? T.green : report.score >= 55 ? T.amber : T.magenta} style={{ fontSize: 12.5, padding: '5px 12px' }}>
              Health {report.score}/100
            </GBIBadge>
          )}
          {report.markdown && (
            <button onClick={() => downloadMd(report.markdown!, clientName)} style={{
              background: 'transparent', border: `1px solid ${T.border}`, borderRadius: 8,
              padding: '6px 12px', fontSize: 12, color: T.fgSecondary, cursor: 'pointer',
              fontFamily: sharedStyles.fontBody, whiteSpace: 'nowrap',
            }}>
              Download .md
            </button>
          )}
        </div>
      </div>

      {/* Full-width style-isolated report (renders exactly like standalone).
          flexShrink: 0 is load-bearing — this wrapper is a column flex container,
          so a default-shrinking iframe gets squeezed back to the leftover space
          (~600px) no matter what height the report posts, which resurrects the
          inner scrollbar the height bridge exists to remove. */}
      <iframe
        ref={iframeRef}
        title="GBP optimization report"
        srcDoc={report.html}
        style={{ width: '100%', height, flexShrink: 0, border: 'none', display: 'block', background: T.bgSurface }}
      />
    </div>
  );
}

function statusLabel(job: AutomationStatus): string {
  if (job.status === 'waiting_quota') return 'Waiting for Claude subscription quota';
  if (job.status === 'waiting_report' || job.currentStep === 'gbp_report') return 'Generating report';
  if (job.currentStep === 'maps_scan') return 'Waiting for Maps scan';
  if (job.status === 'failed' || job.status === 'needs_input') return 'Report preparation needs attention';
  return 'Preparing data';
}

function downloadMd(md: string, name?: string) {
  const blob = new Blob([md], { type: 'text/markdown' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${(name || 'gbp').replace(/\s+/g, '-').toLowerCase()}-action-plan.md`;
  a.click();
  URL.revokeObjectURL(url);
}
